import { useCoach } from '@aj/coach';
import type { Schedule, StudyDate } from '@aj/core';
import { useMemo } from 'react';

import { useAppData } from '../../data/AppDataProvider.js';
import { useActions, useTodayView } from '../../data/hooks.js';
import { useT } from '../../providers/I18nProvider.js';
import type { HabitPlan } from './Compose.js';
import { moveTo } from './move.js';
import type { MessageKey } from '@aj/i18n';

/**
 * Everything both Home layouts do, defined once.
 *
 * The phone and the desktop dashboard show different screens, not the same
 * screen restyled — but they write the same events, and a divergence between
 * them would be the exact kind of bug nobody notices until a user reports it
 * on one device only.
 */

/** The starter pack. Stands in for the real template catalogue (§19). */
const STARTER: MessageKey[] = [
  'starter.read',
  'starter.math',
  'starter.revise',
  'starter.sleep',
  'starter.exercise',
];

export function useHomeActions() {
  const t = useT();
  const coach = useCoach();
  const view = useTodayView();
  const actions = useActions();
  const { state } = useAppData();

  /** Schedules aren't on `HabitRowView` yet; read them straight off state
   *  rather than widening the selector before we know the subtext earns
   *  its place. */
  const schedules = useMemo<Record<string, Schedule>>(
    () => Object.fromEntries(Object.values(state.habits).map((h) => [h.id, h.schedule])),
    [state.habits],
  );

  return {
    schedules,

    // The coach waits on this rather than on a Next button: an onboarding
    // step that says "tap Add habit" is only satisfied by a habit existing.
    addHabit: (name: string, plan?: HabitPlan) => {
      void actions.createHabit({ name, ...plan });
      coach.completed('habit.created');
    },

    addStarterPack: () => {
      for (const key of STARTER) void actions.createHabit({ name: t(key) });
    },

    toggle: (habitId: string, date?: StudyDate) => void actions.toggleHabit(habitId, date),

    /** The two-minute version. Same write, a different level — so it keeps the
     *  streak, counts as a kept promise, and scores a fraction. */
    keepFloor: (habitId: string) => void actions.toggleHabit(habitId, undefined, 'floor'),

    /** Undated on purpose: a task typed on Home is today's work, and asking
     *  for a date before the first one exists is friction §4 doesn't want. */
    addTask: (name: string) => void actions.createTask({ name }),

    toggleTask: (taskId: string) => void actions.toggleTask(taskId),

    /**
     * One drop, one event carrying the whole new order. Chattier than a delta,
     * but it merges by the same last-write-wins rule as everything else and
     * needs no draft state to fall out of sync with a write from another tab.
     */
    reorder: (from: number, to: number) => {
      const next = moveTo(
        view.habits.map((habit) => habit.id),
        from,
        to,
      );
      if (next) void actions.reorderHabits(next);
    },
  };
}
